import React, { Component, ComponentType, ReactNode } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IChannel } from "@formant/core/model/IChannel";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IEventSort } from "@formant/core/model/IEventSort";
import { IFilter } from "@formant/core/model/IFilter";
import { Uuid } from "@formant/core/model/Uuid";

import { notificationEventTypes } from "../../store/SeekOption";
import {
    appStore,
    channelStore,
    deviceDetailsStore,
    observeStore,
    routerStore,
} from "../../stores";
import { Header } from "../common/Header";
import { IconButton } from "../common/IconButton";
import { EventList } from "../EventList";
import { EditEventFilter } from "../EventList/EditEventFilter";
import {
    emptyEventListState,
    IEventListState,
} from "../EventList/EventListState";
import { EventSort } from "../EventList/EventSort";
import { NotFound } from "../NotFound";
import { SearchField } from "../SearchField";
import * as styles from "./index.scss";

export interface ICustomHeaderProps {
    title: string;
    className: string;
    children?: ReactNode;
}

export type CustomHeaderComponent = ComponentType<ICustomHeaderProps>;

export interface IEventsProps {
    deviceId?: Uuid;
    deviceIds?: Uuid[];
    channelId?: Uuid;
    CustomHeader?: CustomHeaderComponent;
}

@observer
export class Events extends Component<IEventsProps> {
    @observable
    private mobileFiltersVisible: boolean = false;
    private device: IDeviceDetails | false | undefined;
    private channel?: IChannel | false | undefined;

    public render() {
        const {
            deviceId,
            channelId,
            deviceIds,
            CustomHeader = Header,
        } = this.props;
        const device = deviceId
            ? (this.device = deviceDetailsStore.get(deviceId))
            : undefined;
        const channel = channelId
            ? (this.channel = channelStore.get(channelId))
            : undefined;

        if (device === false || channel === false) {
            return <NotFound />;
        }

        if (
            (deviceId && !device) ||
            (channelId && !channel) ||
            (deviceIds && !deviceIds.length)
        ) {
            return false;
        }

        const { eventListState, title, filter, onChangeEventFilter } = this;
        const onSelectDeviceRequired = !(deviceId || channelId);
        const deviceImplied = filter.deviceIds?.length === 1;

        const {
            onChangeKeyword,
            onChangeEventListState,
            onSelect,
            onClose,
            onToggleFilters,
            mobileFiltersVisible,
        } = this;

        const filteredEventList = {
            ...eventListState,
            filter: {
                ...("eventTypes" in eventListState.filter
                    ? {
                          ...eventListState.filter,
                          eventTypes: eventListState.filter.eventTypes,
                      }
                    : {
                          ...eventListState.filter,
                          eventTypes: notificationEventTypes,
                      }),
            },
        };
        return (
            <div className={styles.events}>
                <CustomHeader title={title} className={styles.header}>
                    <SearchField
                        value={filteredEventList.filter.keyword || ""}
                        onChange={onChangeKeyword}
                        className={styles.search}
                    />
                    <IconButton
                        icon="filter"
                        onClicked={onToggleFilters}
                        className={styles["filter-button"]}
                    />
                    {CustomHeader === Header ? (
                        <IconButton
                            icon="close-large"
                            onClicked={onClose}
                            className={styles.close}
                        />
                    ) : null}
                </CustomHeader>
                <EditEventFilter
                    filter={filteredEventList.filter}
                    context={filter}
                    onChange={onChangeEventFilter}
                    hideDeviceFilter={deviceImplied}
                    className={classnames(
                        styles.filters,
                        mobileFiltersVisible && styles.visible,
                    )}
                />
                <EventSort
                    sort={filteredEventList.sort}
                    hideDevice={deviceImplied}
                    onChangeSort={this.onSort}
                />
                <EventList
                    context={filter}
                    eventListState={filteredEventList}
                    onChange={onChangeEventListState}
                    onSelect={onSelect}
                    onSelectDeviceRequired={onSelectDeviceRequired}
                />
            </div>
        );
    }

    private get eventListState(): IEventListState {
        const { deviceId, channelId } = this.props;
        const { allEventsListState } = appStore;

        if (deviceId) {
            return (
                appStore.deviceEventListState[deviceId] || emptyEventListState
            );
        } else if (channelId) {
            return (
                appStore.channelEventListState[channelId] || emptyEventListState
            );
        } else {
            return allEventsListState;
        }
    }

    private get title(): string {
        const { device, channel } = this;
        if (device) {
            return device.name;
        } else if (channel) {
            return channel.name;
        } else {
            return "Events";
        }
    }

    private get filter(): IFilter {
        const { deviceId, deviceIds } = this.props;
        const { channel } = this;
        if (deviceId) {
            return {
                deviceIds: [deviceId],
            };
        } else if (channel) {
            return channel.filter;
        } else if (deviceIds) {
            return {
                deviceIds,
            };
        } else {
            return {};
        }
    }

    private onSort = (sort: IEventSort) => {
        const { eventListState, onChangeEventListState } = this;
        onChangeEventListState({
            ...eventListState,
            sort,
            page: 0,
        });
    };

    private onChangeEventFilter = (filter: IEventFilter) => {
        const { eventListState, onChangeEventListState } = this;
        onChangeEventListState({
            ...eventListState,
            filter,
            page: 0,
        });
    };

    private onChangeKeyword = (keyword: string) => {
        const { eventListState, onChangeEventListState } = this;
        const state = {
            ...eventListState,
            filter: {
                ...eventListState.filter,
                keyword,
            },
            page: 0,
        };

        onChangeEventListState(state);
    };

    private onChangeEventListState = (eventListState: IEventListState) => {
        const { deviceId, channelId } = this.props;
        if (deviceId) {
            appStore.deviceEventListState[deviceId] = eventListState;
        } else if (channelId) {
            appStore.channelEventListState[channelId] = eventListState;
        } else {
            appStore.allEventsListState = eventListState;
        }
    };

    private onSelect = async (event: IEvent) => {
        const { channelId } = this.props;
        await observeStore.navigateToEvent(event, channelId);
    };

    private onClose = async () => {
        const { deviceId, channelId } = this.props;
        const { navigate, routeTo } = routerStore;

        if (deviceId) {
            await navigate(routeTo.device({ id: deviceId }));
        } else if (channelId) {
            await navigate(routeTo.channel({ id: channelId }));
        } else {
            await navigate(routeTo.home());
        }
    };

    private onToggleFilters = () => {
        this.mobileFiltersVisible = !this.mobileFiltersVisible;
    };
}
